import animations from "./animations";
import { BREAKPOINTS } from "../styles/variables/breakpoints";

const setPositionPlanes = (planes, planesGroup, isMobileRef) => {
  const radius = isMobileRef.current ? 180 : 300;
  const totalPlanes = planes.length;
  const scaleFactor = 0.03;
  const svgWidth = isMobileRef.current ? 350 : 500;
  const svgHeight = isMobileRef.current ? 350 : 500;

  planes.forEach((plane, index) => {
    const angle = (index / totalPlanes) * Math.PI * 2;

    const x = (isMobileRef.current ? 175 : 250) + radius * Math.cos(angle);
    const y = (isMobileRef.current ? 175 : 250) + radius * Math.sin(angle);

    const centeredX = (x - svgWidth / 2) * scaleFactor;
    const centeredY = -(y - svgHeight / 2) * scaleFactor;

    plane.position.set(centeredX, centeredY, 0);
    plane.userData.position = {
      x: centeredX,
      y: centeredY,
      z: 0,
    };

    const rotationAngle = Math.atan2(centeredY, centeredX);
    plane.rotation.z = rotationAngle;
  });

  const angleCorrection = -Math.atan2(
    planes[0].position.y,
    planes[0].position.x
  );

  planesGroup.rotation.z = angleCorrection;

  if (isMobileRef.current) {
    planesGroup.position.set(-5.4, 0, 0);
  } else {
    planesGroup.position.set(-9, 0, 0);
  }
};

const animateTransitionNavigation = (
  navigationItems,
  positions,
  direction,
  fromIndex,
  toIndex,
  delay = 0,
  labelPrefix = "",
  mm
) => {
  const n = navigationItems.length;
  const timeline = animations.timeline({});
  if (n < 2 || fromIndex === toIndex) return timeline;

  const lblSwitch = `${labelPrefix}-switch`;
  const lblZ = `${labelPrefix}-reindex`;
  const lblSettle = `${labelPrefix}-settle`;

  const exitY = direction === 1 ? -85 : 100;
  const enterY = direction === 1 ? 100 : -85;

  mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
    timeline
      .to(
        navigationItems[fromIndex],
        { delay, y: exitY, filter: "blur(3px)" },
        lblSwitch
      )
      .to(
        navigationItems[toIndex],
        { delay, y: enterY, filter: "blur(0px)" },
        lblSwitch
      )
      .addLabel(lblZ, `>`)
      .add(() => {
        navigationItems.forEach((item, i) => {
          const offset = (i - toIndex + n) % n;
          const z = n - offset;
          animations.set(item, { zIndex: z });
        });
      }, lblZ);
    navigationItems.forEach((item, i) => {
      timeline.to(
        item,
        {
          x: positions[i]?.x ?? 0,
          y: positions[i]?.y ?? 0,
        },
        lblSettle
      );
    });
  });

  return timeline;
};

const animatePlanes = (
  planes,
  planesGroup,
  setHoveredIndex,
  handleSetPlanesAnimating,
  activeAnimation,
  setLastPlane,
  isMobileRef,
  lenisRef,
  mm
) => {
  const totalPlanes = planes.length;
  const navigationItems = document.querySelectorAll('div[id^="navigation-"]');
  const appWrapper = document.querySelector(".app-wrapper");
  const lenisScroll = document.querySelector("#lenis-scroll");

  const bodegaPlane = planes[planes.length - 1];

  let bodegaLandingContainer, bodegaScene, bodegaImage, bodegaHeading;

  if (bodegaPlane && bodegaPlane.element) {
    const { element } = bodegaPlane;

    bodegaLandingContainer = element.querySelector(".bodega-landing-container");
    bodegaScene = element.querySelector(".bodega-landing-scene");
    bodegaImage = element.querySelector(".bodega-landing-image");
    bodegaHeading = element.querySelector(".bodega-landing-text");
  }

  let observerInstance, scrollTriggerInstance;
  let hasEntered = false;

  if (totalPlanes < 2 || navigationItems.length < 2) return;

  const anglePerPlane = (Math.PI * 2) / totalPlanes;

  const getLabelInner = index =>
    planesGroup.children[index]?.children[0]?.element?.querySelector(
      ".label-inner"
    );

  const state = { currentIndex: 0 };
  let animating,
    bodegaAnimating,
    bodegaBlurAnimationLanding,
    bodegaBlurAnimationExit,
    bodegaAnimationCompleted = false;

  const getPositions = () =>
    Array.from(navigationItems).map((item, index) => ({
      x: animations.getProperty(item, "x"),
      y: animations.getProperty(item, "y"),
      zIndex: navigationItems.length - index,
    }));

  const gotoPlane = (newIndex, direction) => {
    if (newIndex < 0 || newIndex >= totalPlanes || animating) return;

    const fromIndex = state.currentIndex;
    const toIndex = newIndex;
    const positions = getPositions();

    const fromIndexLabel = getLabelInner(fromIndex);
    const toIndexLabel = getLabelInner(toIndex);
    const bodegaSubheading = document.querySelector(".bodega-subheading");

    animating = true;
    handleSetPlanesAnimating(true);

    if (newIndex === totalPlanes - 1) {
      bodegaBlurAnimationLanding = true;
      setLastPlane(true);
    } else if (fromIndex === totalPlanes - 1) {
      bodegaBlurAnimationExit = true;
      setLastPlane(false);
    }

    const timeline = animations.timeline({
      defaults: { duration: 3, ease: "power1.inOut" },
      onStart: () => {
        setHoveredIndex(null);
      },
      onComplete: () => {
        positions[fromIndex].zIndex = navigationItems.length - 1;
        positions[toIndex].zIndex = navigationItems.length;

        animating = false;
        bodegaBlurAnimationLanding = false;
        bodegaBlurAnimationExit = false;
        state.currentIndex = toIndex;
        handleSetPlanesAnimating(false);
      },
    });

    timeline.to(planesGroup.rotation, { z: anglePerPlane * newIndex }, 0);

    if (fromIndexLabel) {
      timeline.to(
        fromIndexLabel,
        { duration: 0.25, delay: 0.25, filter: "blur(3px)" },
        0
      );
    }

    if (bodegaSubheading && bodegaBlurAnimationExit) {
      timeline.to(
        bodegaSubheading,
        { duration: 0.25, delay: 0.25, filter: "blur(3px)" },
        0
      );
    }

    if (toIndexLabel) {
      timeline.to(
        toIndexLabel,
        {
          delay: 0.25,
          duration: 0.25,
          filter: "blur(0px)",
        },
        "step-label"
      );
    }

    if (bodegaSubheading && bodegaBlurAnimationLanding) {
      timeline.to(
        bodegaSubheading,
        {
          delay: 0.25,
          duration: 0.25,
          filter: "blur(0px)",
        },
        "step-label"
      );
    }

    timeline.add(() => {
      animateTransitionNavigation(
        navigationItems,
        positions,
        direction,
        fromIndex,
        toIndex,
        0.5,
        `step-${fromIndex}-${toIndex}`,
        mm
      );
    }, 0);
  };

  const bodegaTimeline = animations.timeline({
    paused: true,
    defaults: { duration: 1, ease: "power2.out" },
    onStart: () => {
      bodegaAnimating = true;
    },
    onComplete: () => {
      bodegaAnimating = false;
    },
    onReverseComplete: () => {
      bodegaAnimationCompleted = false;
      bodegaAnimating = false;
    },
  });

  if (bodegaScene && bodegaImage && bodegaLandingContainer) {
    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      bodegaTimeline
        .to(bodegaScene, { width: "100%", height: "100%" }, 0)
        .to(bodegaImage, { top: 35, left: 0, width: "100%", height: "100%" }, 0)
        .addLabel("opacityEffect", "<=0.5")
        .to(bodegaLandingContainer, { opacity: 0 }, "opacityEffect")
        .to(bodegaHeading, { opacity: 1 }, "opacityEffect");
    });
  }

  const handleOnUp = () => {
    if (!activeAnimation.current) return;

    const isAtLastPlane = state.currentIndex === totalPlanes - 1;
    const canPlayBodega =
      isAtLastPlane &&
      !animating &&
      !bodegaAnimationCompleted &&
      !isMobileRef.current;

    if (canPlayBodega) {
      bodegaAnimationCompleted = true;
      bodegaTimeline?.play();
      return;
    }

    if (!bodegaAnimationCompleted) {
      gotoPlane(state.currentIndex + 1, 1);
    }
  };

  const handleOnDown = () => {
    if (!activeAnimation.current) return;
    if (bodegaAnimationCompleted && !bodegaAnimating && !isMobileRef.current) {
      bodegaAnimating = true;
      bodegaTimeline.reverse();
      return;
    }

    const isFirstPlane = state.currentIndex === 0;
    if (isFirstPlane && !animating) {
      observerInstance.disable();
      handleSetPlanesAnimating(false);
      setTimeout(() => {
        lenisRef?.current?.start?.();
      }, 50);
      hasEntered = false;
      return;
    }

    if (!bodegaAnimating) {
      gotoPlane(state.currentIndex - 1, -1);
    }
  };

  const handleOnChange = () => {
    if (animating || !activeAnimation.current || bodegaAnimationCompleted)
      return;
    handleSetPlanesAnimating(true);
    setHoveredIndex(null);
  };

  mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
    observerInstance = animations.ScrollTrigger.observe({
      target: lenisScroll,
      type: "wheel,scroll",
      wheelSpeed: -0.5,
      tolerance: 50,
      scroller: lenisScroll,
      preventDefault: true,
      onUp: () => handleOnUp(),
      onDown: () => handleOnDown(),
    });

    scrollTriggerInstance = animations.ScrollTrigger.create({
      trigger: appWrapper,
      start: "top 10%",
      end: "bottom 5%",
      scroller: lenisScroll,
      id: "scrollTriggerInstance",
      onEnter: () => {
        if (!hasEntered) {
          hasEntered = true;
          observerInstance?.enable();
          setTimeout(() => lenisRef?.current?.stop?.(), 50);
        }
      },
      onEnterBack: () => {
        if (!hasEntered) {
          hasEntered = true;
          observerInstance?.enable();
          setTimeout(() => lenisRef?.current?.stop?.(), 50);
        }
      },
      onLeaveBack: () => {
        if (!hasEntered || state.currentIndex !== 0 || animating) return;
        hasEntered = false;
        observerInstance?.disable();
        setTimeout(() => lenisRef?.current?.start?.(), 50);
      },
      onLeave: () => {
        if (!hasEntered || state.currentIndex !== 0 || animating) return;
        hasEntered = false;
        observerInstance?.disable();
        setTimeout(() => lenisRef?.current?.start?.(), 50);
      },
    });
  });

  mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
    observerInstance = animations.ScrollTrigger.observe({
      target: appWrapper,
      type: "wheel,scroll,touch,pointer",
      wheelSpeed: -0.5,
      tolerance: 25,
      preventDefault: true,
      onUp: () => handleOnUp(),
      onDown: () => handleOnDown(),
      onChange: () => handleOnChange(),
    });

    scrollTriggerInstance = animations.ScrollTrigger.create({
      trigger: appWrapper,
      start: "top top",
      pin: true,
      anticipatePin: 1,
      id: "scrollTriggerInstanceMobile",
      onEnter: self => {
        observerInstance?.enable();
        self.scroll(self.start + 1);
      },
      onEnterBack: self => {
        self.scroll(self.end - 1);
        observerInstance?.enable();
      },
      onLeaveBack: () => {
        if (animating) return;
        observerInstance?.disable();
      },
      onLeave: () => {
        if (animating) return;
        observerInstance?.disable();
      },
    });
  });

  observerInstance?.disable();

  requestAnimationFrame(() => {
    setTimeout(() => {
      animations.ScrollTrigger.refresh();
    }, 100);
  });

  return {
    enable: () => scrollTriggerInstance?.enable(),
    disable: () => scrollTriggerInstance?.disable(),
    destroy: () => {
      scrollTriggerInstance?.kill();
      observerInstance?.kill();
    },
  };
};

export { setPositionPlanes, animatePlanes };
